/**
 * NightSleepBatchModal
 *
 * Bottom-sheet modal for batch-adding night sleep phases.
 * Shown in the morning (before 12:00) via a "Nacht nachtragen" button.
 * Each row has a from-time and to-time input.
 * Starts with one row, dashed placeholder to add more.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Moon, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';

interface SleepPhaseRow {
  id: number;
  from: string;
  to: string;
}

interface NightSleepBatchModalProps {
  onClose: () => void;
  onSave: (phases: Array<{ from: string; to: string }>) => Promise<void>;
}

let rowIdCounter = 1;

export function NightSleepBatchModal({ onClose, onSave }: NightSleepBatchModalProps) {
  const { t } = useTranslation('sleepPlan');
  const [rows, setRows] = useState<SleepPhaseRow[]>([
    { id: rowIdCounter++, from: '', to: '' },
  ]);
  const [isSaving, setIsSaving] = useState(false);

  const addRow = () => {
    setRows(prev => [...prev, { id: rowIdCounter++, from: '', to: '' }]);
  };

  const updateRow = (id: number, field: 'from' | 'to', value: string) => {
    setRows(prev =>
      prev.map(row => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (id: number) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter(row => row.id !== id));
  };

  const validRows = rows.filter(row => row.from && row.to);
  const canSave = validRows.length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    await onSave(validRows.map(r => ({ from: r.from, to: r.to })));
    setIsSaving(false);
    onClose();
  };

  return createPortal(
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal" style={{ maxHeight: '85vh' }}>
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                <Moon className="w-5 h-5 text-indigo-400" />
              </div>
              <h2>{t('nightBatch.title')}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-3 overflow-y-auto" style={{ maxHeight: '50vh' }}>
            <p className="text-white/60 text-sm">
              {t('nightBatch.description')}
            </p>

            {/* Sleep phase rows */}
            {rows.map((row, index) => (
              <div key={row.id} className="glass-effect rounded-[15px] p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/50 text-xs font-medium">
                    {t('nightBatch.phase', { number: index + 1 })}
                  </span>
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(row.id)}
                      className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/20 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-white/50 text-xs">{t('nightBatch.from')}</label>
                    <input
                      type="time"
                      value={row.from}
                      onChange={(e) => updateRow(row.id, 'from', e.target.value)}
                      className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-indigo-400 focus:outline-none"
                    />
                  </div>
                  <span className="text-white/50 mt-4">-</span>
                  <div className="flex-1">
                    <label className="text-white/50 text-xs">{t('nightBatch.to')}</label>
                    <input
                      type="time"
                      value={row.to}
                      onChange={(e) => updateRow(row.id, 'to', e.target.value)}
                      className="w-full p-2 bg-white/10 rounded-[10px] text-white text-center border border-white/20 focus:border-indigo-400 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Dashed placeholder to add more */}
            <button
              onClick={addRow}
              className="w-full py-4 border-2 border-dashed border-white/20 rounded-[15px] flex items-center justify-center gap-2 text-white/40 hover:text-white/60 hover:border-white/30 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">{t('nightBatch.addPhase')}</span>
            </button>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
            >
              {t('nightBatch.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave || isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('nightBatch.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
